/**
 * The attribute-operator vocabulary. One declaration is the source of truth
 * for every derived read: the dropdown option groups in attribute-filter.ts
 * and the per-surface allow-lists the condition rows build from `family`.
 *
 * Values match the backend's canonical spelling (services core/operators.go
 * `Opstring()`). `arity` is how many values the operator takes on its
 * right-hand side and agrees with the backend's `IsZeroValue`/`IsTwoValue`;
 * negations inherit it by construction.
 *
 * Negation is a pairing, not wire syntax: the `!` prefix is this UI's
 * spelling, while the wire carries a base operator wrapped in `{ not: ... }`.
 * Each format plugin owns that encoding.
 */

export type OperatorArity = 0 | 1 | 2;

// The semantic grouping the surfaces compose allow-lists from. Distinct from a
// select-group heading, which is per-surface (see attribute-filter.ts).
export type OperatorFamily =
  | 'equality'
  | 'inequality'
  | 'existence'
  | 'string'
  | 'dateExistence'
  | 'dateInequality'
  | 'extraDateInequality'
  | 'email'
  | 'array'
  | 'oneOf';

export type OperatorOption = { value: string; label: string };

type OperatorDefinition = {
  value: string;
  label: string;
  arity: OperatorArity;
  family: OperatorFamily;
  negation?: OperatorOption;
};

export const OPERATOR_DEFINITIONS = [
  {
    value: 'eq',
    label: 'is equal to',
    arity: 1,
    family: 'equality',
    negation: { value: '!eq', label: 'is not equal to' },
  },
  { value: 'gt', label: 'is greater than', arity: 1, family: 'inequality' },
  { value: 'lt', label: 'is less than', arity: 1, family: 'inequality' },
  { value: 'bw', label: 'is between', arity: 2, family: 'inequality' },
  {
    value: 'exists',
    label: 'exists',
    arity: 0,
    family: 'existence',
    negation: { value: '!exists', label: 'does not exist' },
  },
  {
    value: 'contains',
    label: 'contains',
    arity: 1,
    family: 'string',
    negation: { value: '!contains', label: 'does not contain' },
  },
  {
    value: 'timestamp',
    label: 'is a timestamp',
    arity: 0,
    family: 'dateExistence',
    negation: { value: '!timestamp', label: 'is not a timestamp' },
  },
  { value: 'timestamp_gt', label: 'is a timestamp after', arity: 1, family: 'dateInequality' },
  { value: 'timestamp_lt', label: 'is a timestamp before', arity: 1, family: 'dateInequality' },
  // Absolute timestamp operators: same labels as the relative pair, reached
  // through a variant select rather than the operator dropdown.
  { value: 'timestamp_gta', label: 'is a timestamp after', arity: 1, family: 'dateInequality' },
  { value: 'timestamp_lta', label: 'is a timestamp before', arity: 1, family: 'dateInequality' },
  { value: 'timestamp_bw', label: 'is a timestamp between', arity: 2, family: 'extraDateInequality' },
  {
    value: 'email_valid',
    label: 'is valid and exists',
    arity: 0,
    family: 'email',
    negation: { value: '!email_valid', label: "is not valid or doesn't exist" },
  },
  {
    value: 'contained_in_array',
    label: 'is contained in',
    arity: 1,
    family: 'array',
    negation: { value: '!contained_in_array', label: 'is not contained in' },
  },
  {
    value: 'one_of',
    label: 'is one of',
    arity: 1,
    family: 'oneOf',
    negation: { value: '!one_of', label: 'is not one of' },
  },
] as const satisfies readonly OperatorDefinition[];

const ARITY_BY_OPERATOR: Map<string, OperatorArity> = (() => {
  const arities = new Map<string, OperatorArity>();
  for (const definition of OPERATOR_DEFINITIONS) {
    arities.set(definition.value, definition.arity);
    if ('negation' in definition) {
      // A negation takes the same values as the operator it negates.
      arities.set(definition.negation.value, definition.arity);
    }
  }
  return arities;
})();

// A '!' prefix on a UI operator does not always mean a declared negation: the
// wire negates by wrapping a filter in `{ not: ... }`, and unwrapping that
// prefixes whatever operator it held — including ones with no negated form of
// their own, like `bw`. Negating never changes arity or family, so both reads
// fall back to the base operator.
function lookUp<TValue>(table: Map<string, TValue>, operator: string): TValue | undefined {
  const exact = table.get(operator);
  if (exact !== undefined) {
    return exact;
  }
  return operator.startsWith('!') ? table.get(operator.slice(1)) : undefined;
}

// How many values the operator takes, or undefined for anything outside the
// vocabulary. Tolerant on purpose: the callers are checking wire payloads and
// user input, where an unrecognized operator is a case to handle rather than a
// programming error.
export function operatorArity(operator: string | null | undefined): OperatorArity | undefined {
  if (operator == null) {
    return undefined;
  }
  return lookUp(ARITY_BY_OPERATOR, operator);
}

const FAMILY_BY_OPERATOR: Map<string, OperatorFamily> = (() => {
  const families = new Map<string, OperatorFamily>();
  for (const definition of OPERATOR_DEFINITIONS) {
    families.set(definition.value, definition.family);
    if ('negation' in definition) {
      families.set(definition.negation.value, definition.family);
    }
  }
  return families;
})();

// The family a declared operator belongs to, or undefined for anything outside
// the vocabulary. A declared negation shares its base's family.
//
// Strict where `operatorArity` is tolerant, and the asymmetry is deliberate:
// callers use this to decide whether a surface can *offer* an operator, so a
// `not`-wrapped `bw` arriving as `!bw` must read as unknown — there is no `!bw`
// to put in a dropdown. Arity, by contrast, is still knowable for it.
export function familyOfOperator(operator: string | null | undefined): OperatorFamily | undefined {
  if (operator == null) {
    return undefined;
  }
  return FAMILY_BY_OPERATOR.get(operator);
}

// Whether a surface that edits these families can edit this operator. Listing
// families positively is deliberate: a family added to the vocabulary later is
// unsupported until someone says otherwise, because a row must never offer an
// operator its dropdown can't render.
export function isOperatorInFamilies(operator: string, families: ReadonlySet<OperatorFamily>): boolean {
  const family = familyOfOperator(operator);
  return family != null && families.has(family);
}

export function isOneOfOperator(operator: string | null | undefined): boolean {
  return operator === 'one_of' || operator === '!one_of';
}

// The dateInequality family pairs each relative operator (value = signed
// seconds from now, negative = ago) with an absolute variant (value = a unix
// timestamp). Only the relative pair appears in the operator dropdown, so the
// pairing is vocabulary data, not view state.
const TIMESTAMP_VARIANTS = [
  { relative: 'timestamp_gt', absolute: 'timestamp_gta' },
  { relative: 'timestamp_lt', absolute: 'timestamp_lta' },
] as const;

export type TimestampVariant = 'relative' | 'absolute';

export function timestampVariantOf(operator: string): TimestampVariant | undefined {
  for (const pair of TIMESTAMP_VARIANTS) {
    if (pair.relative === operator) {
      return 'relative';
    }
    if (pair.absolute === operator) {
      return 'absolute';
    }
  }
  return undefined;
}

// Every operator in a family, each negation directly after its base — the
// order the dropdown option arrays have always been in.
export function operatorsInFamily(family: OperatorFamily): OperatorOption[] {
  const options: OperatorOption[] = [];
  for (const definition of OPERATOR_DEFINITIONS) {
    if (definition.family !== family) {
      continue;
    }
    options.push({ value: definition.value, label: definition.label });
    if ('negation' in definition) {
      options.push({ value: definition.negation.value, label: definition.negation.label });
    }
  }
  return options;
}
